/**
 * Каталог товаров v2 — константы и хелперы.
 */

import type { BadgeVariant } from '../../components/ui/Badge'
import {
  isDraftCard,
  targetsFromCards,
} from '../../components/marketplace-publish/publishScope'
import type { PublishCardTarget } from '../../components/marketplace-publish/types'
import { maketsPreviewUrl } from '../../shared/media/pipelineFiles'
import { normalizeMarketplace } from '../../shared/marketplaces'
import type { FiltersV2, MarketplaceCardRow, PresenceState, ProductV2 } from './types'

// ── Справочники ─────────────────────────────────────────────────────

/** Статусы карточки МП (marketplace_cards.status) */
export const CARD_STATUS_INFO: Record<string, { label: string; variant: BadgeVariant }> = {
  draft: { label: 'Черновик PIM', variant: 'gray' },
  creating: { label: 'Отправляется', variant: 'gray' },
  created: { label: 'Создана на МП', variant: 'blue' },
  price_set: { label: 'Цена установлена', variant: 'yellow' },
  stock_set: { label: 'Коммерция только в PIM', variant: 'yellow' },
  selling: { label: 'Историческая отметка PIM', variant: 'gray' },
  error: { label: 'Ошибка', variant: 'red' },
  // Строка списка собрана по зеркалу площадки: карточки ПИМ у связанного импортом товара нет
  linked: { label: 'На витрине (связана)', variant: 'green' },
}

/** Итоговое состояние присутствия на площадке (GET /products/:article/presence, поле cards[].state) */
export const PRESENCE_STATE_INFO: Record<PresenceState, { label: string; variant: BadgeVariant }> = {
  live: { label: 'На витрине', variant: 'green' },
  draft: { label: 'Черновик ПИМ', variant: 'gray' },
  publishing: { label: 'Отправляется', variant: 'blue' },
  error: { label: 'Ошибка', variant: 'red' },
  excluded: { label: 'Исключена из выгрузки', variant: 'yellow' },
  archived: { label: 'В архиве', variant: 'gray' },
  none: { label: 'Нет', variant: 'gray' },
}

export const PRESENCE_MARKETPLACE_LABEL: Record<string, string> = {
  wb: 'WB',
  ozon: 'OZON',
  ym: 'Я.Маркет',
  site: 'Сайт',
}

export const ONE_C_INFO: Record<string, { label: string; dot: string }> = {
  pending_1c: { label: 'Ждёт 1С', dot: 'bg-gray-400' },
  synced: { label: 'В 1С', dot: 'bg-green-500' },
}

// ── Кабинеты WB ─────────────────────────────────────────────────────

/** Короткие имена кабинетов WB и порядок в выборе */
const WB_CABINET_SHORT: Record<string, string> = {
  ksa_wb: 'КСА',
  kma_wb: 'КМА',
  svv_wb: 'СВВ',
}

/**
 * Кабинеты WB для фильтра каталога: только продавцы WB, короткие имена.
 *
 * Площадка сверяется реестром, а не литералом: в `mp_sellers.marketplace`
 * лежит «wildberries», и сравнение с `'wb'` давало на проде пустой список —
 * в выборе оставался один пункт «Все» (16.09.2026).
 *
 * Незнакомый кабинет не теряется: подписывается именем продавца без
 * префикса площадки и встаёт в конец.
 */
export function wbCabinetOptions(
  sellers: Array<{ id: number; name: string; marketplace?: string; cabinet_code?: string | null }>,
): Array<{ id: number; label: string }> {
  const order = Object.keys(WB_CABINET_SHORT)
  const rank = (code?: string | null) => {
    const index = code ? order.indexOf(code) : -1
    return index === -1 ? order.length : index
  }
  return sellers
    .filter((seller) => normalizeMarketplace(seller.marketplace) === 'wb')
    .sort((a, b) => rank(a.cabinet_code) - rank(b.cabinet_code))
    .map((seller) => ({
      id: seller.id,
      label:
        (seller.cabinet_code && WB_CABINET_SHORT[seller.cabinet_code]) ||
        seller.name.replace(/^(ВБ|WB)\s+/i, ''),
    }))
}

/**
 * Фильтр кабинета имеет смысл только вместе с условием по WB: сервер
 * применяет sellerId внутри условий площадки, и кабинет WB в условии OZON
 * дал бы «нет нигде».
 */
export function isWbCabinetScope(marketplace: string): boolean {
  return marketplace === 'wb' || marketplace === 'no_wb'
}

// ── Связи с маркетплейсами ──────────────────────────────────────────

export function isLinkedToWb(product: ProductV2): boolean {
  return product.wb_nm_id != null && String(product.wb_nm_id).length > 0
}

export function isLinkedToOzon(product: ProductV2): boolean {
  const offer = product.ozon_offer_id ?? product.ozon_product_id
  return offer != null && String(offer).length > 0
}

export function isLinkedToYm(product: ProductV2): boolean {
  return product.ym_market_sku != null && String(product.ym_market_sku).length > 0
}

/** Товар прогнан конвейером, но не выгружен: есть карточка МП в статусе «Черновик» */
export function hasDraftCard(product: ProductV2): boolean {
  return (product.cards || []).some(isDraftCard)
}

/**
 * Товары → карточки для выгрузки. Из каталога товаров область действия «все
 * площадки»: уезжают все черновики товара, включая обе площадки сразу.
 */
export function productPublishTargets(products: ProductV2[]): PublishCardTarget[] {
  return products.flatMap((product) => targetsFromCards(product.article, product.cards, 'all'))
}

/**
 * Сводка выборки для операции «Создать карточки сайта».
 *
 * Единица прогона конвейера — макет, а выделяют в каталоге товары, поэтому в
 * окне подтверждения нужны обе цифры. Товар без макета в конвейер не идёт:
 * визуалы рендерятся из макета, и заводить ему карточку нечем.
 *
 * Числа здесь — оценка для человека. Окончательный отбор (в том числе уже
 * заведённые карточки сайта) делает бэкенд и возвращает в ответе.
 */
export function siteCardsSelectionSummary(products: ProductV2[]): {
  articles: string[]
  designs: string[]
  withoutDesign: string[]
} {
  const articles: string[] = []
  const designs = new Set<string>()
  const withoutDesign: string[] = []
  for (const product of products) {
    const article = String(product.article || '').trim()
    if (!article) continue
    const designCode = String(product.design_id ?? product.design?.design_code ?? '').trim()
    if (
      product.design?.origin === 'external' ||
      !designCode ||
      isNoDesignCode(designCode)
    ) {
      withoutDesign.push(article)
      continue
    }
    articles.push(article)
    designs.add(designCode)
  }
  return { articles, designs: [...designs], withoutDesign }
}

/** Карточка МП с самым «продвинутым» статусом — для колонки «Карточки МП» */
/** Служебный код макета у товаров без макета: это не номер, показывать его нельзя. */
export const NO_DESIGN_CODE = 'NODESIGN'

/** `NODESIGN` — товар без макета (в любом регистре, с пробелами). */
export function isNoDesignCode(code: unknown): boolean {
  return String(code ?? '').trim().toUpperCase() === NO_DESIGN_CODE
}

export function primaryCard(product: ProductV2): MarketplaceCardRow | null {
  const cards = product.cards || []
  if (!cards.length) return null
  const rank = ['error', 'creating', 'created', 'price_set', 'stock_set', 'selling']
  return [...cards].sort((a, b) => rank.indexOf(b.status) - rank.indexOf(a.status))[0]
}

// ── Превью ──────────────────────────────────────────────────────────

export type ProductPreviewSource = 'print' | 'product' | 'design' | 'none'

/** Источник показанного изображения не равен состоянию файла печати. */
export function productPreviewInfo(product: ProductV2): {
  media: ReturnType<typeof productImageMediaInternal>
  source: ProductPreviewSource
  sourceLabel: string
} {
  const resolved = productImageMediaInternal(product)
  if (!resolved) return { media: null, source: 'none', sourceLabel: 'Нет изображения' }
  if (resolved.type === 'print-preview') {
    return { media: resolved, source: 'print', sourceLabel: 'Превью печати' }
  }
  if (resolved.type === 'product-image') {
    return { media: resolved, source: 'product', sourceLabel: 'Фото товара' }
  }
  return { media: resolved, source: 'design', sourceLabel: 'Превью макета' }
}

/**
 * Изображение каталога выбирается независимо от наличия макета:
 * готовое print-preview → собственное фото товара → превью связанного макета.
 */
function productImageMediaInternal(product: ProductV2) {
  const printPreview = printPreviewSource(product)
  if (printPreview) {
    return {
      id: `print-${product.print_artifact?.id ?? product.article}`,
      url: printPreview.url,
      type: 'print-preview',
      isMain: 1,
      // Готовый рендер конвейера отдаётся как есть, а хранилище умеет ресайз:
      // плитке хватает 64 пикселей, и просить ради неё полный файл незачем.
      resizable: printPreview.resizable,
    }
  }
  const ownImage = Array.isArray(product.image_urls)
    ? product.image_urls.find((url) => typeof url === 'string' && url.trim())
    : null
  if (ownImage) {
    return {
      id: `product-${product.article}`,
      url: ownImage,
      type: 'product-image',
      isMain: 1,
      resizable: false,
    }
  }
  return designPreviewMedia(product)
}

/**
 * Превью самого файла печати. Источник — оригинал файла в хранилище макетов
 * (`storage_path`): маршрут `makets-file` строит из него миниатюру нужной
 * ширины на лету и кладёт результат в постоянный дисковый кэш `_thumbs`, для
 * PDF в том числе (PT1 и PT2 — там файл печати и есть исходник в PDF).
 *
 * `preview_url` — только запасной путь на случай пустого `storage_path`.
 * Раньше было наоборот, и это была причина массовой битой плитки в каталоге
 * 08.09.2026: `preview_url` указывает во ВРЕМЕННЫЙ кэш движка
 * (`production/_previews/...`), который уборщик кэша сносит раз в сутки —
 * любая ссылка туда обречена протухнуть в течение суток после генерации.
 * Хранилище смонтировано read-only и оригинал из него никуда не девается.
 */
function printPreviewSource(product: ProductV2): { url: string; resizable: boolean } | null {
  const artifact = product.print_artifact
  if (artifact?.status !== 'ready') return null
  const fromStorage = maketsPreviewUrl(artifact.storage_path)
  if (fromStorage) return { url: fromStorage, resizable: true }
  if (artifact.preview_url) return { url: artifact.preview_url, resizable: true }
  return null
}

/** Превью связанного макета: основное фото, PDF-исходник в плитку не годится. */
function designPreviewMedia(product: ProductV2) {
  const media = product.design?.media
  if (!media?.length) return null
  const found = media.find((m) => m.isMain === 1 && m.type !== 'pdf')
    || media.find((m) => m.type !== 'pdf')
  // Маршрут медиа макета ресайз умеет, но ширину подставляет сам потребитель
  // (ImagePreviewThumb): адрес здесь остаётся тем же, что у карточки макета.
  return found ? { ...found, resizable: true } : null
}

export function productImageMedia(product: ProductV2) {
  return productPreviewInfo(product).media
}

/**
 * Превью строки таблицы: печать → макет, без публичного визуала воронки.
 *
 * Маркетинговое фото в узкой плитке можно принять за файл печати — его убрали
 * из строки 12.08.2026. Превью макета такой путаницы не создаёт: печатается
 * именно оно, поэтому пустая плитка у товара с готовым файлом — потеря, а не
 * осторожность. Режим «фото» (PhotoGrid) показывает полную лестницу: там
 * картинка крупная и подписана.
 */
export function catalogRowPreview(product: ProductV2): {
  media: ReturnType<typeof productImageMediaInternal>
  source: Exclude<ProductPreviewSource, 'product'>
  sourceLabel: string
} {
  const info = productPreviewInfo(product)
  if (info.source === 'print') return { media: info.media, source: 'print', sourceLabel: info.sourceLabel }
  const design = designPreviewMedia(product)
  if (design) return { media: design, source: 'design', sourceLabel: 'Превью макета' }
  return { media: null, source: 'none', sourceLabel: 'Нет изображения' }
}

export function printArtifactInfo(product: ProductV2): {
  label: string
  variant: BadgeVariant
  reason?: string | null
} {
  const artifact = product.print_artifact
  if (!artifact) return { label: 'Нет файла', variant: 'gray' }
  if (artifact.status === 'ready') {
    // Отсутствие `preview_url` состояние файла не меняет: миниатюру рисует
    // маршрут хранилища прямо из оригинала, PDF в том числе. Жёлтый бейдж
    // оставляем на случай, когда рисовать нечего — файла нет и в хранилище.
    return printPreviewSource(product)
      ? { label: 'Готов', variant: 'green' }
      : {
          label: 'Готов, без файла',
          variant: 'yellow',
          reason: 'Артефакт помечен готовым, но путь к файлу в хранилище не записан',
        }
  }
  if (artifact.status === 'failed') {
    return { label: 'Ошибка', variant: 'red', reason: artifact.error || 'Не удалось создать файл' }
  }
  if (artifact.status === 'generating') return { label: 'Генерируется', variant: 'yellow' }
  return { label: 'Ожидает', variant: 'gray' }
}

// ── Форматирование ──────────────────────────────────────────────────

export function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU')
}

/** «450 ₽» с неразрывным пробелом-разделителем, как требует дизайн-система */
export function formatPrice(value?: number | string | null): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `${n.toLocaleString('ru-RU')} ₽`
}

export function formatDate(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
}

/** Название товара для списков */
export function productTitle(product: ProductV2): string {
  return product.title?.trim() || product.article
}

// ── Фильтры ─────────────────────────────────────────────────────────

/** Количество активных фильтров панели «Все фильтры» (для бейджа) */
export function countPanelFilters(f: FiltersV2): number {
  let n = 0
  if (f.designType.length) n++
  if (f.designCategory.length) n++
  if (f.brand.length) n++
  if (f.theme.length) n++
  if (f.persona.length) n++
  if (f.gender.length) n++
  if (f.age.length) n++
  if (f.occasion.length) n++
  if (f.seasonal) n++
  if (f.legal.length) n++
  if (f.designState.length) n++
  if (f.preview) n++
  if (f.barcode) n++
  if (f.oneC) n++
  if (f.priceFrom || f.priceTo) n++
  if (f.origin) n++
  return n
}

export function hasAnyFilters(f: FiltersV2): boolean {
  return Boolean(
    f.search ||
      f.category.length ||
      f.brand.length ||
      f.designType.length ||
      f.designCategory.length ||
      f.theme.length ||
      f.persona.length ||
      f.marketplace ||
      f.sellerId ||
      f.tagIds.length ||
      f.noTags ||
      f.dateFrom ||
      f.dateTo ||
      countPanelFilters(f) > 0,
  )
}

// ── localStorage ────────────────────────────────────────────────────

export function loadLS<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export function saveLS(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // квота или приватный режим — молча пропускаем
  }
}

export const LS_KEYS = {
  view: 'productsV2.view',
  mode: 'productsV2.mode',
  grouping: 'productsV2.grouping',
  savedViews: 'productsV2.savedViews',
  pageSize: 'productsV2.pageSize',
  columnsOrder: 'productsV2.catalogColumns.order',
  columnsVisible: 'productsV2.catalogColumns.visible',
  columnsWidths: 'productsV2.catalogColumns.widths',
  columnsDefaultsVersion: 'productsV2.catalogColumns.defaultsVersion',
}
